import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { CourseListItem, EventListItem, GigListItem, LabListItem, ProjectListItem } from '../../types/content';
import Button from '../../components/UI/Button';
import { canApprove, canReview, canReviewEvents, canReviewProjects } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import ActionStatus from '../../components/UI/ActionStatus';

type Reviewable = {
  requester?: string;
  summary?: string;
  status?: string;
};

const requestMeta = (item: { reviewRequest?: { requesterName?: string | null; requesterRole?: string | null; changeSummary?: string | null; submittedAt?: string | null }; author?: { displayName?: string }; status?: string }): Reviewable => ({
  requester: item.reviewRequest?.requesterName
    ? `${item.reviewRequest.requesterName}${item.reviewRequest.requesterRole ? ` · ${item.reviewRequest.requesterRole}` : ''}`
    : item.author?.displayName,
  summary: item.reviewRequest?.changeSummary || undefined,
  status: item.status,
});

const ReviewQueuePage: React.FC = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [labs, setLabs] = useState<LabListItem[]>([]);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [gigs, setGigs] = useState<GigListItem[]>([]);
  const [events, setEvents] = useState<EventListItem[]>([]);
  const [comments, setComments] = useState('');
  const [status, setStatus] = useState<{ key: string; message: string } | null>(null);
  useEffect(() => {
    if (!status) return undefined;
    const timer = window.setTimeout(() => setStatus(null), 5000);
    return () => window.clearTimeout(timer);
  }, [status]);

  const load = () => {
    contentApi.courseQueue().then((r) => setCourses(r.data.courses || [])).catch(() => undefined);
    contentApi.labQueue().then((r) => setLabs(r.data.labs || [])).catch(() => undefined);
    if (canReviewProjects(user)) {
      contentApi.projectQueue().then((r) => setProjects(r.data.projects || [])).catch(() => undefined);
      contentApi.gigQueue().then((r) => setGigs(r.data.gigs || [])).catch(() => undefined);
    }
    if (canReviewEvents(user)) {
      contentApi.eventQueue().then((r) => setEvents(r.data.events || [])).catch(() => undefined);
    }
  };

  useEffect(() => { load(); }, [user]);

  if (!canReview(user) && !canApprove(user) && !canReviewProjects(user) && !canReviewEvents(user)) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-gray-600">You do not have review access.</div>;
  }

  const mark = (key: string, message: string) => setStatus({ key, message });

  const details = (item: Reviewable) => (
    <div className="mt-2 text-sm text-gray-600 space-y-1">
      <p><span className="font-medium text-gray-800">Requester:</span> {item.requester || 'Unknown'}</p>
      {item.summary ? <p><span className="font-medium text-gray-800">Requested change:</span> {item.summary}</p> : null}
      {item.status ? <p><span className="font-medium text-gray-800">Status:</span> {item.status}</p> : null}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold">Review Queue</h1>
      <p className="mt-2 text-gray-600">
        Course Admin adds review comments and recommends for Owner publish. Only Owner can give final approval and publish.
        Course Owners see review comments on their editor and resubmit after edits.
      </p>
      <textarea className="mt-4 w-full rounded-md border-gray-300" rows={2} value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Review comments" />
      <h2 className="mt-8 font-semibold">Courses</h2>
      <div className="space-y-3 mt-3">
        {courses.map((course) => (
          <div key={course.id} className="rounded-xl border p-4">
            <Link to={`/my-courses/${course.id}/edit`} className="font-medium text-primary-700 hover:text-primary-800">
              {course.title}
            </Link>
            {details(requestMeta(course))}
            <div className="mt-2 flex gap-2 flex-wrap">
              <Link to={`/my-courses/${course.id}/edit`}><Button type="button" variant="outline">Open</Button></Link>
              <Button type="button" variant="outline" onClick={async () => { await contentApi.reviewCourse(course.id, 'ChangesRequested', comments); mark(`course-${course.id}`, 'Changes requested.'); load(); }}>Request changes</Button>
              <Button type="button" variant="secondary" onClick={async () => { await contentApi.reviewCourse(course.id, 'Approved', comments); mark(`course-${course.id}`, 'Review approved.'); load(); }}>Approve review (send to Owner)</Button>
              {canApprove(user) && <Button type="button" onClick={async () => { await contentApi.approveCourse(course.id, comments); mark(`course-${course.id}`, 'Published.'); load(); }}>Owner approve & publish</Button>}
            </div>
            <ActionStatus message={status?.key === `course-${course.id}` ? status.message : null} />
          </div>
        ))}
        {courses.length === 0 && <p className="text-sm text-gray-500">No courses waiting for review.</p>}
      </div>
      <h2 className="mt-8 font-semibold">Labs</h2>
      <div className="space-y-3 mt-3">
        {labs.map((lab) => (
          <div key={lab.id} className="rounded-xl border p-4">
            <Link to={`/admin/labs/${lab.id}/edit`} className="font-medium text-primary-700 hover:text-primary-800">
              {lab.name}
            </Link>
            {details(requestMeta(lab))}
            <div className="mt-2 flex gap-2 flex-wrap">
              <Link to={`/admin/labs/${lab.id}/edit`}><Button type="button" variant="outline">Open</Button></Link>
              <Button type="button" variant="outline" onClick={async () => { await contentApi.reviewLab(lab.id, 'ChangesRequested', comments); mark(`lab-${lab.id}`, 'Changes requested.'); load(); }}>Request changes</Button>
              <Button type="button" variant="secondary" onClick={async () => { await contentApi.reviewLab(lab.id, 'Approved', comments); mark(`lab-${lab.id}`, 'Review approved.'); load(); }}>Approve review (send to Owner)</Button>
              {canApprove(user) && <Button type="button" onClick={async () => { await contentApi.approveLab(lab.id, comments); mark(`lab-${lab.id}`, 'Published.'); load(); }}>Owner approve & publish</Button>}
            </div>
            <ActionStatus message={status?.key === `lab-${lab.id}` ? status.message : null} />
          </div>
        ))}
        {labs.length === 0 && <p className="text-sm text-gray-500">No labs waiting for review.</p>}
      </div>
      {canReviewProjects(user) && (
        <>
          <h2 className="mt-8 font-semibold">Projects</h2>
          <div className="space-y-3 mt-3">
            {projects.map((gig) => (
              <div key={gig.id} className="rounded-xl border p-4">
                <Link to={`/admin/projects/${gig.id}/edit`} className="font-medium text-primary-700 hover:text-primary-800">
                  {gig.title}
                </Link>
                {details(requestMeta(gig))}
                <div className="mt-2 flex gap-2 flex-wrap">
                  <Link to={`/admin/projects/${gig.id}/edit`}><Button type="button" variant="outline">Open</Button></Link>
                  <Button type="button" variant="outline" onClick={async () => { await contentApi.reviewProject(gig.id, 'ChangesRequested', comments); mark(`project-${gig.id}`, 'Changes requested.'); load(); }}>Request changes</Button>
                  <Button type="button" variant="secondary" onClick={async () => { await contentApi.reviewProject(gig.id, 'Approved', comments); mark(`project-${gig.id}`, 'Review approved.'); load(); }}>Approve review (send to Owner)</Button>
                  {canApprove(user) && <Button type="button" onClick={async () => { await contentApi.approveProject(gig.id, comments); mark(`project-${gig.id}`, 'Published.'); load(); }}>Owner approve & publish</Button>}
                </div>
                <ActionStatus message={status?.key === `project-${gig.id}` ? status.message : null} />
              </div>
            ))}
            {projects.length === 0 && <p className="text-sm text-gray-500">No projects waiting for review.</p>}
          </div>
          <h2 className="mt-8 font-semibold">Gigs</h2>
          <div className="space-y-3 mt-3">
            {gigs.map((gig) => (
              <div key={gig.id} className="rounded-xl border p-4">
                <Link to={`/admin/gigs/${gig.id}/edit`} className="font-medium text-primary-700 hover:text-primary-800">
                  {gig.title}
                </Link>
                {details(requestMeta(gig))}
                <div className="mt-2 flex gap-2 flex-wrap">
                  <Link to={`/admin/gigs/${gig.id}/edit`}><Button type="button" variant="outline">Open</Button></Link>
                  <Button type="button" variant="outline" onClick={async () => { await contentApi.reviewGig(gig.id, 'ChangesRequested', comments); mark(`gig-${gig.id}`, 'Changes requested.'); load(); }}>Request changes</Button>
                  <Button type="button" variant="secondary" onClick={async () => { await contentApi.reviewGig(gig.id, 'Approved', comments); mark(`gig-${gig.id}`, 'Recommended.'); load(); }}>Recommend</Button>
                  {canApprove(user) && <Button type="button" onClick={async () => { await contentApi.approveGig(gig.id, comments); mark(`gig-${gig.id}`, 'Published.'); load(); }}>Owner approve & publish</Button>}
                </div>
                <ActionStatus message={status?.key === `gig-${gig.id}` ? status.message : null} />
              </div>
            ))}
            {gigs.length === 0 && <p className="text-sm text-gray-500">No gigs waiting for review.</p>}
          </div>
        </>
      )}
      {canReviewEvents(user) && (
        <>
          <h2 className="mt-8 font-semibold">Events</h2>
          <div className="space-y-3 mt-3">
            {events.map((event) => (
              <div key={event.id} className="rounded-xl border p-4">
                <Link to={`/admin/events/${event.id}/edit`} className="font-medium text-primary-700 hover:text-primary-800">
                  {event.title}
                </Link>
                {details(requestMeta(event))}
                <div className="mt-2 flex gap-2 flex-wrap">
                  <Link to={`/admin/events/${event.id}/edit`}><Button type="button" variant="outline">Open</Button></Link>
                  <Button type="button" variant="outline" onClick={async () => { await contentApi.reviewEvent(event.id, 'ChangesRequested', comments); mark(`event-${event.id}`, 'Changes requested.'); load(); }}>Request changes</Button>
                  <Button type="button" variant="secondary" onClick={async () => { await contentApi.reviewEvent(event.id, 'Approved', comments); mark(`event-${event.id}`, 'Recommended.'); load(); }}>Recommend</Button>
                  {canApprove(user) && <Button type="button" onClick={async () => { await contentApi.approveEvent(event.id, comments); mark(`event-${event.id}`, 'Published.'); load(); }}>Owner approve & publish</Button>}
                </div>
                <ActionStatus message={status?.key === `event-${event.id}` ? status.message : null} />
              </div>
            ))}
            {events.length === 0 && <p className="text-sm text-gray-500">No events waiting for review.</p>}
          </div>
        </>
      )}
    </div>
  );
};

export default ReviewQueuePage;
